import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
@safeCustomElement('mdc-radio-button')
export class MdcRadioButton extends LitElement {
  static formAssociated = true

  @property({ type: Boolean }) checked = false
  @property({ type: Boolean }) disabled = false
  @property({ type: Boolean }) required = false
  @property({ type: String }) name = ''
  @property({ type: String }) value = ''
  @property({ type: String }) label = 'Radio Button'
  @property({ type: Object }) validation?: { status: 'error' | 'success'; message?: string }

  private _internals!: ElementInternals

  constructor() {
    super()
    this._internals = this.attachInternals()
  }

  connectedCallback() {
    super.connectedCallback()
    this._internals.setFormValue(this.checked ? this.value : null)
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties)
    if (
      changedProperties.has('checked') ||
      changedProperties.has('value') ||
      changedProperties.has('name')
    ) {
      this._internals.setFormValue(this.checked ? this.value : null)
      this._updateValidity()
    }
  }

  private _updateValidity() {
    const input = this.shadowRoot?.querySelector('input')
    if (input) {
      this._internals.setValidity(input.validity, input.validationMessage, input)
    }
  }

  formResetCallback() {
    this.checked = false
    this._internals.setFormValue(null)
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled
  }

  formStateRestoreCallback(state: string) {
    this.value = state
    this.checked = true
    this._internals.setFormValue(this.value)
  }

  static styles = css`
		:host {
			display: inline-block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-radio-button {
			display: inline-flex;
			align-items: center;
			gap: var(--spacing-050, 0.5rem);
			cursor: pointer;
			user-select: none;
		}

		.mdc-radio-button__input {
			position: absolute;
			opacity: 0;
			width: 0;
			height: 0;
		}

		.mdc-radio-button__box {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 1.25rem;
			height: 1.25rem;
			border: 1px solid var(--color-functional-gray-400);
			border-radius: var(--radius-full, 9999px);
			background-color: var(--color-functional-gray-white);
			transition: background-color 0.2s, border-color 0.2s;
			flex-shrink: 0;
		}

		.mdc-radio-button__input:checked + .mdc-radio-button__box {
			background-color: var(--color-functional-gray-black);
			border-color: var(--color-functional-gray-black);
		}

		.mdc-radio-button__input:checked + .mdc-radio-button__box mdc-icon {
			opacity: 1;
			visibility: visible;
			color: var(--color-functional-gray-white);
		}

		.mdc-radio-button__box mdc-icon {
			display: block;
			opacity: 0;
			visibility: hidden;
			width: 0.625rem;
			height: 0.625rem;
		}

		.mdc-radio-button__input:disabled + .mdc-radio-button__box {
			background-color: var(--color-functional-gray-50);
			border-color: var(--color-functional-gray-200);
			cursor: not-allowed;
		}

		.mdc-radio-button__input:disabled ~ .mdc-radio-button__label {
			color: var(--color-functional-gray-300);
			cursor: not-allowed;
		}

		.mdc-radio-button__input[aria-invalid='true'] + .mdc-radio-button__box {
			border-color: var(--color-functional-red-500);
		}

		.mdc-radio-button__label {
			font-size: var(--text-087-and-half, 0.875rem);
			font-weight: 450;
			line-height: 1.3;
			color: var(--color-functional-gray-black);
		}
	`

  private handleChange(e: Event) {
    const input = e.target as HTMLInputElement
    this.checked = input.checked
    this._internals.setFormValue(this.checked ? this.value : null)
    this._updateValidity()
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { checked: this.checked },
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    return html`
			<label class="mdc-radio-button">
				<input
					type="radio"
					class="mdc-radio-button__input"
					name=${this.name}
					.value=${this.value}
					?checked=${this.checked}
					?disabled=${this.disabled}
					?required=${this.required}
					aria-invalid=${this.validation?.status === 'error' ? 'true' : undefined}
					@change=${this.handleChange}
				/>
				<div class="mdc-radio-button__box">
					<mdc-icon name="checkmark"></mdc-icon>
				</div>
				${this.label ? html`<span class="mdc-radio-button__label">${this.label}</span>` : ''}
			</label>
		`
  }
}
